<template>
    <div class="layout-main-container">
        <div class="layout-main">
            <div style="text-align: center;">
                <Dialog header="Header" v-model:visible="displayChangePhotoDialog" :modal="true">
                    <template #header>
                        <div></div>
                    </template>
                    <FileUpload :multiple="false" :customUpload="true" @uploader="uploadPhoto" accept="image/*"
                        :maxFileSize="5000000" :uploadLabel="$t('UPLOAD')" :chooseLabel="$t('CHOOSE')"
                        :cancelLabel="$t('CANCEL')">
                        <template #empty>
                            <div class="text-blue-500" style="text-align: center;">
                                <p>{{ $t('DRAG_FOTO_MESSAGE') }}</p>
                                <i :class="['pi', 'pi-cloud-upload']" style="font-size: 2.5rem;" />
                            </div>
                        </template>
                    </FileUpload>
                </Dialog>
            </div>
            <Dialog v-model:visible="displayWillDialog" :modal="true"
                :breakpoints="{ '960px': '75vw', '640px': '90vw' }">
                <div class="flex flex-wrap justify-content-center" style="direction: rtl;">
                    <div v-if="!!userInfo.willLastUpdate">
                        <span>
                            {{ $t('LAST_UPDATE') }}:
                        </span>
                        <span>
                            {{ utils.formatDate(userInfo.willLastUpdate) }}
                        </span>
                    </div>
                    <Textarea style="width: 100%;margin-top: 10px;" v-model="userInfo.willDetail" rows="10"
                        maxlength="4000" />
                    <Button style="margin-top: 10px;" :disabled="!canRegisterWill" :label="$t('SAVE_WILL')"
                        @click="saveWill" />
                </div>
            </Dialog>
            <div class="justify-content-center">
                <div class="card mx-auto lg:w-8">
                    <div class="flex flex-wrap justify-content-center w-full mt-5 px-5" dir="rtl">
                        <div class="row flex flex-column w-full lg:w-6">
                            <label for="basic" class="column">{{ $t('FIRST_NAME') }}</label>
                            <InputText id="firstname" type="text" disabled="true" v-model="userInfo.firstname" />
                        </div>
                        <div class="row flex flex-column w-full lg:w-6">
                            <label for="lastname" class="column">{{ $t('LAST_NAME') }}</label>
                            <InputText id="lastname" type="text" disabled="true" v-model="userInfo.lastname" />
                        </div>
                        <div class="row flex flex-column w-full lg:w-6">
                            <label for="email" class="column">{{ $t('EMAIL') }}</label>
                            <InputText id="email" type="text" disabled="true" v-model="userInfo.email" />
                        </div>
                        <div v-if="!authStore.amIAdmin()" class="row flex flex-column w-full lg:w-6">
                            <label for="score" class="column">{{ $t('SCORE') }}</label>
                            <InputText id="score" type="text" disabled="true" v-model="userInfo.score" />
                        </div>
                        <div v-if="!authStore.amIAdmin()" class="row flex flex-column w-full lg:w-6">
                            <label for="evaluationPoints" class="column">{{ $t('EVALUATION_POINTS') }}</label>
                            <InputText id="evaluationPoints" type="text" disabled="true"
                                v-model="userInfo.evaluationPoints" />
                        </div>
                        <div v-if="!authStore.amIAdmin()" class="row flex flex-column w-full lg:w-6">
                            <label for="rewardablePoints" class="column">{{ $t('REWARDABLE_POINTS') }}</label>
                            <InputText id="rewardablePoints" type="text" disabled="true"
                                v-model="userInfo.rewardablePoints" />
                        </div>
                    </div>
                    <div class="flex flex-wrap  logout-buttons justify-content-between mb-5 mt-2 ml-1 px-5 gap-2" dir="rtl">
                        <Button :label="$t('CHANGE_PHOTO')" @click="showChangePhotoDialog" class="w-full lg:w-3 " />
                        <Button @click="takePicture" class="w-full justify-content-center lg:w-3 p-0">
                            <i :class="['pi', 'pi-camera']" style="font-size: 1rem;"></i>
                            <span style="margin: 8px;">{{ $t('TAKE_FOTO') }}</span>
                        </Button>
                        <Button :label="$t('CHANGE_PASSWORD')" @click="showChangePasswordDialog" class="w-full lg:w-3" />
                        <Button v-if="!authStore.amIAdmin()" :label="$t('WILL')" @click="showWillDialog" class="w-full lg:w-2"/>
                        <Button :label="$t('DELETE_ACCOUNT')" @click="showDeleteAccountDialog" class="w-full lg:w-3" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref, onErrorCaptured, computed } from "vue"
import { handleError } from '@/helpers/ErrorHandler'
import { useToast } from "primevue/usetoast"
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useDialog } from 'primevue/usedialog'
import PasswordChanger from '@/components/PasswordChanger.vue'
import userManagementService from '@/service/UserManagementService'
import userService from '@/service/UserService'
import { Camera, CameraResultType } from '@capacitor/camera'
import ServerError from "@/model/ServerError"
import { genericServerMessageEnum } from '@/helpers/Constants'
import utils from '@/helpers/Utils'
import { useConfirm } from 'primevue/useconfirm'

const toast = useToast()
const i18n = useI18n()
const authStore = useAuthStore()
const confirm = useConfirm()

const dialog = useDialog()

const userInfo = ref({})

const displayChangePhotoDialog = ref(false)
const displayWillDialog = ref(false)

onMounted(async () => {
    try {
        const response = await userManagementService.getUserInfo()
        userInfo.value = response.result
        userService.calculateRewardablePoints(userInfo.value)
    } catch (error) {
        handleError(authStore, toast, i18n, error)
    }
})

onErrorCaptured((err, instance, info) => {
    return handleError(authStore, toast, i18n, err, instance, info)
})


const showChangePhotoDialog = () => {
    displayChangePhotoDialog.value = true
}

const uploadPhoto = async (event) => {
    const blob = await fetch(event.files[0].objectURL).then(r => r.blob())
    const response = await userManagementService.uploadProfilePhoto(blob, event.files[0].name)
    await authStore.pullUserData()
    displayChangePhotoDialog.value = false
    toast.add({ severity: 'info', summary: response.message, detail: '', life: 3000 })
}

const showChangePasswordDialog = () => {
    dialog.open(PasswordChanger, {
        inject: ['dialogRef'],
        props: {
            header: i18n.t('CHANGE_PASSWORD'),
            style: {
                width: '20em',
            },
            modal: true
        },
        emits: {
            onError: (e) => {
                if (e instanceof ServerError) {
                    toast.add({ severity: 'error', summary: e.detail, detail: '', life: 3000 })
                } else {
                    handleError(authStore, toast, i18n, e)
                }
            }, onPasswordChanged: (m) => {
                toast.add({ severity: 'info', summary: m, detail: '', life: 3000 })
            }, onValidationError: (e) => {
                toast.add({ severity: 'error', summary: e, detail: '', life: 3000 })
            }
        }
    })
}

const takePicture = async () => {
    const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Base64
    })
    const blob = dataURIToBlob(image.base64String, 'image/' + image.format)
    const response = await userManagementService.uploadProfilePhoto(blob, 'my_foto')
    await authStore.pullUserData()
    displayChangePhotoDialog.value = false
    toast.add({ severity: 'info', summary: response.message, detail: '', life: 3000 })
}

function dataURIToBlob(base64Image, mimeString) {
    const byteString = atob(base64Image)
    const ia = new Uint8Array(byteString.length)
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
    }
    return new Blob([ia], { type: mimeString })
}

const showWillDialog = () => {
    displayWillDialog.value = true
}

const canRegisterWill = computed(() => {
    return !!userInfo.value.willDetail && userInfo.value.willDetail.length > 0
})

const saveWill = async () => {
    const response = await userService.saveWill(userInfo.value.willDetail)
    if (response.message === genericServerMessageEnum.OK) {
        userInfo.value.willLastUpdate = response.result.willLastUpdate
        toast.add({ severity: 'info', summary: i18n.t('WILL_SAVED'), detail: '', life: 3000 })
    }
}

const showDeleteAccountDialog = () => {
    confirm.require({
        header: i18n.t('CONFIRMATION_HEADER'),
        message: i18n.t('DELETE_ACCOUNT_CONFIRM_MESSAGE'),
        acceptLabel: i18n.t('YES'),
        rejectLabel: i18n.t('NO'),
        icon: 'pi pi-exclamation-triangle',
        accept: async () => {
            try {
                const response = await userService.deleteAccount()
                if (response.message === genericServerMessageEnum.OK) {
                    toast.add({ severity: 'info', summary: i18n.t('USER_DELETED_SUCCESSFULLY'), detail: '', life: 3000 })
                    authStore.logout();
                }
            } catch (error) {
                handleError(authStore, toast, i18n, error)
            }
        }
    });
}

</script>

<style scoped>
.card {
  border: 1px solid var(--surface-border);
  padding: 0.5rem !important;
  margin-bottom: 2rem;
  box-shadow: var(--card-shadow);
  border-radius: 12px;

  &:last-child {
    margin-bottom: 0;
  }
}

.row>* {
    margin: 0 0 0.5em 0.5em;
}


:deep(.p-fileupload-file-remove) {
    display: none;
}


</style>